/**
 * API utility functions for making server requests
 */
import { getVehicleId } from "./formatting";

/**
 * Captures booking information and sends it to the server
 * @param {Object} appointment - The appointment data
 * @returns {Promise} - Promise with the response data
 */
export const captureBooking = async function (appointment) {
  const captureUrl = `${import.meta.env.VITE_API_URL}/bookings/capture`;

  const capturePayload = {
    domain: appointment?.domain,
    siteId: appointment?.shopData?.shopId,
    fname: appointment?.customer?.fname,
    lname: appointment?.customer?.lname,
    number: appointment?.customer?.phone,
    email: appointment?.customer?.email,
    apptType: appointment?.apptType,
    duration: appointment?.service?.hours,
    title: appointment?.service?.name,
    bailOutStage: appointment?.bailOutStage,
    comments: appointment?.comments,
    date: appointment.date,
    time: appointment.time,
    service: appointment.service,
    newAppointmentId: appointment.bookingId,
    bailed: appointment.bailed,
    complimentaryRide: appointment.complimentaryRide,
    afterHoursDropOffRequested:
      appointment.apptType === "drop"
        ? Boolean(appointment.afterHoursDropOffRequested)
        : false,
    sourceQueryString: appointment?.sourceQueryString,
  };

  try {
    // First fetch call for capture
    const captureResponse = await fetch(captureUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(capturePayload),
    });

    if (!captureResponse.ok) {
      throw new Error(
        "Network response was not ok " + captureResponse.statusText
      );
    }

    const captureData = await captureResponse.json();
    return captureData;
  } catch (error) {
    console.error("Error posting booking config:", error);
  }
};

/**
 * Posts booking configuration to the server
 * @param {Object} appointment - The appointment data
 * @returns {Promise} - Promise with the response data
 */
export const postBookingConfig = async function (appointment) {
  console.log("postBookingConfig", appointment);
  const bookingUrl = `${import.meta.env.VITE_API_URL}/bookings/book?domain=${appointment.domain}&shopId=${appointment.shopData?.shopId}`;

  let rawMessage =
    appointment?.shopData?.messages?.confirmationMessage ||
    "Your appointment has been successfully booked! We look forward to seeing you soon.";
  let message = rawMessage.replace(/{{new_line}}/g, "<br />");

  if (appointment?.service) {
    message = message.replace(
      /{{your_service}}/g,
      "your " + appointment?.service + " service"
    );
  } else {
    message = message.replace(/{{your_service}}/g, "");
  }

  if (appointment?.date) {
    message = message.replace(/{{on_date}}/g, " on " + appointment?.date);
  } else {
    message = message.replace(/{{on_date}}/g, "");
  }

  if (appointment?.time) {
    message = message.replace(/{{at_time}}/g, " at " + appointment?.time);
  } else {
    message = message.replace(/{{at_time}}/g, "");
  }

  message = message.replace(/\s+\?/g, "?");

  try {
    const bookingPayload = {
      apptType: appointment.apptType,
      fname: appointment.customer?.fname,
      lname: appointment.customer?.lname,
      email: appointment.customer?.email,
      number: appointment.customer?.phone,
      phoneNumber: appointment.customer?.phone,

      address1: appointment.customer?.address?.street,
      city: appointment.customer?.address?.city,
      state: appointment.customer?.address?.state,
      zip: appointment.customer?.address?.zip,

      date: appointment.date,
      time: appointment.time,

      year: appointment.vehicle?.year,
      make: appointment.vehicle?.make,
      model: appointment.vehicle?.model,
      vehicleId: getVehicleId(appointment.vehicle),

      integrationId: appointment.customerId,
      dcId: appointment.dcId,
      service: appointment.service,
      hours: appointment.serviceHours,
      comments: appointment.comments,
      serviceComments: "",
      optOut: appointment.customer?.optOut ?? true,
      siteId: appointment.shopData?.shopId,

      deferredServices: appointment?.deferredServices,

      newAppointmentId: appointment.bookingId,
      rwg_token: appointment.rwg_token,

      confirmationMessage: message,
      services: appointment?.services,
      complimentaryRide: appointment.complimentaryRide,
      afterHoursDropOffRequested:
        appointment.apptType === "drop"
          ? Boolean(appointment.afterHoursDropOffRequested)
          : false,
      sourceQueryString: appointment?.sourceQueryString,
    };

    const bookingResponse = await fetch(bookingUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(bookingPayload),
    });

    let responseData = {};
    try {
      responseData = await bookingResponse.json();
    } catch (error) {
      responseData = {};
    }

    if (!bookingResponse.ok) {
      const errorMessage =
        responseData?.message ||
        responseData?.error ||
        "Network response was not ok " + bookingResponse.statusText;
      const bookingError = new Error(errorMessage);
      bookingError.status = bookingResponse.status;
      bookingError.response = responseData;
      throw bookingError;
    }

    return {
      success:
        typeof responseData?.success === "boolean" ? responseData.success : true,
      ceCreated: Boolean(responseData?.ceCreated),
      smsRequired: Boolean(responseData?.smsRequired),
      smsConfirmed: Boolean(responseData?.smsConfirmed),
      confirmed:
        typeof responseData?.confirmed === "boolean"
          ? responseData.confirmed
          : Boolean(responseData?.success),
      message: responseData?.message,
      ...responseData,
    };
  } catch (error) {
    console.error("Error posting booking config:", error);
    throw error;
  }
};

/**
 * Gets available booking times for a given date (V2 API endpoint)
 * @param {Object} appointment - Appointment data containing service details
 * @returns {Promise} - Promise with the available times data
 */
export const getBookingTimesV2 = async function (appointment) {
  const url = encodeURI(
    `${import.meta.env.VITE_API_URL}/bookings/v2/apptOptions`
  );

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        ...appointment,
        siteId: appointment.shopId,
        duration: appointment.hours,
        appointment_date: appointment.date,
      }),
    });

    if (!response.ok) {
      throw new Error("Failed to fetch data");
    } else {
      return response.json();
    }
  } catch (error) {
    console.error("Error fetching data:", error);
    return { availableSlots: [], unavailableSlots: [] };
  }
};
